<script>
  import { selectedDistrict } from "@stores/userData.js";

  import cityContacts from "@data/contacts.json";
  import CouncilMemberCard from "../Find/CouncilMemberCard.svelte";
  import WriteToCouncilMember from "./WriteToCouncilMember.svelte";

  $: member = $selectedDistrict
    ? cityContacts
        .filter(
          (contact) =>
            contact["Department"] === "City Council" &&
            contact["Position"].includes("District")
        )
        .find((contact) => contact["District"] === $selectedDistrict)
    : null;

  import departmentBudgets from "@data/departmentBudgets.json";
  import {
    topFourDepartments,
    selectedPerformanceMeasures,
  } from "@stores/userData.js";

  $: getBudget = (department) =>
    departmentBudgets?.find((d) => d.name == department)?.budget;

  import { format } from "d3-format";
  const suffixFormat = format(",.3s");

  let departmentIndex = 0;
  $: selectedDepartment = $topFourDepartments[departmentIndex];

  let measureIndex = 0;
  $: selectedMeasure = $selectedPerformanceMeasures[measureIndex];

  const incrementIndex = (name) => {
    if (name == "Measure") {
      measureIndex++;
      if (measureIndex >= $selectedPerformanceMeasures.length) {
        measureIndex = 0;
      }
    }
    if (name == "Department") {
      departmentIndex++;
      if (departmentIndex >= $topFourDepartments.length) {
        departmentIndex = 0;
      }
    }
  };

  $: messages =
    selectedDepartment && selectedMeasure
      ? [
          {
            name: "Department",
            message: `In FY23, can the ${selectedDepartment.Department} receive more funding? I think Houston is better off when ${selectedDepartment.Department} is better-funded.`,
          },
          {
            name: "Measure",
            message: `I think ${selectedMeasure.Measure}, funded by the ${selectedMeasure.Department}, should be prioritized when allocating funds during the FY23 budget cycle.`,
          },
          {
            name: "Missing Outcomes",
            message: `I think the ____ Department should start tracking ____ as a measure of success. What do you think?`,
          },
          {
            name: "Top Four",
            message: `I wanted to put it on your radar that I'd like to see more funding for these departments next budget cycle: ${$topFourDepartments[0].Department}, ${$topFourDepartments[1].Department}, ${$topFourDepartments[2].Department}, and ${$topFourDepartments[3].Department}.`,
          },
          {
            name: "Involvement",
            message: `How can I get more involved in the budget process, and can you tell me about your priorities for the city budget next fiscal year?`,
          },
        ]
      : [];

  let selectedIndex = 0;
  $: selected = messages[selectedIndex];
  $: selectedMessage = selected?.message;

  $: remainingLabel = function (name) {
    if (name == "Department") {
      return `${departmentIndex + 1}/${$topFourDepartments.length}`;
    } else if (name == "Measure") {
      return `${measureIndex + 1}/${$selectedPerformanceMeasures.length}`;
    }
  };
</script>

{#if $selectedDistrict && member}
  <div class="main">
    <div class="flex-child left">
      <CouncilMemberCard {member} isStandalone={true} />
    </div>
    <div class="flex-child right">
      <WriteToCouncilMember {member} {selectedMessage} {selectedIndex} />
    </div>
  </div>
{:else}
  <WriteToCouncilMember {member} {selectedMessage} {selectedIndex} />
{/if}
<div class="messages">
  <p class="messages-title">Want some help? Here are some options:</p>
  <div class="messages-grid">
    {#each messages as message, index}
      <button
        class="message"
        class:active={selectedIndex == index}
        on:click={() => {
          selectedIndex = index;
        }}
      >
        <p>{message.message}</p>
        {#if message.name == "Measure" || message.name == "Department"}
          <span class="arrow" on:click={incrementIndex(message.name)}
            ><span style="font-size: 80%; margin-right: -1px;"
              >{remainingLabel(message.name)}</span
            > &#x267A;</span
          >
        {/if}
      </button>
    {/each}
  </div>
</div>

<style>
  .main {
    display: flex;
    flex-direction: row;
  }

  .flex-child {
    flex: 1;
    padding: 0.5rem;
  }

  @media screen and (max-width: 768px) {
    .main {
      flex-direction: column;
    }
    .flex-child {
      margin-bottom: 0.25rem;
    }
  }

  .messages {
    margin-top: 2rem;
  }

  .messages-title {
    text-align: center;
    margin-bottom: 1rem;
    font-size: 1.3rem;
    font-family: var(--font-serif);
    font-weight: 300;
  }

  .messages-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
  }

  .message {
    padding: 0.75rem;
    background: var(--primary-color-base);
    display: flex;
    place-items: center;
    box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);
    border-radius: 0.25rem;
    transition: background 500ms ease, border-color 300ms ease,
      opacity 300ms ease;
    cursor: pointer;
    position: relative;
    border-width: 2px;
    border-style: solid;
  }

  .message:hover,
  .message.active {
    background: rgba(var(--accent-color-rgb), 0.05);
    opacity: 1;
  }

  .message.active {
    border-color: var(--accent-color);
  }

  .message:not(.active) {
    border-color: transparent;
    opacity: 0.85;
  }

  .arrow {
    user-select: none;
    cursor: pointer;
    position: absolute;
    bottom: 6px;
    right: 6px;
  }

  .message p {
    font-size: 1rem;
    line-height: 1.2;
    font-weight: 400;
    color: var(--text-color);
    user-select: none;
    text-align: left;
  }

  @media screen and (max-width: 568px) {
    .messages-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
